import { ControllerRenderProps } from "react-hook-form";
import MultipleSelector, { Option } from "@/components/ui/multiple-selector";
import { CityResponse, GeoService } from "@/generated-api";
import { useAuthStore } from "@/stores/authStore";
import { refreshOnFailure } from "@/tanstack-hooks";

export function cityLabel(city: CityResponse) {
  if (city.state) {
    return city.name + ", " + city.state + ", " + city.country.name;
  } else {
    return city.name + ", " + city.country.name;
  }
}

const citiesSearch = async (
  value: string,
  accessToken: string | null,
  setToken: (accessToken: string) => void,
  clearToken: () => void
): Promise<Option[]> => {
  if (value.length < 2 || !/^[a-zA-Z .-]+$/.test(value)) {
    return [];
  }
  try {
    const cities: CityResponse[] = await refreshOnFailure(
      () => GeoService.searchCitiesApiGeoSearchCitiesGet(value),
      accessToken,
      setToken,
      clearToken
    );

    const mappedOptions: Option[] = cities.map((city: CityResponse) => ({
      label: cityLabel(city),
      value: city.id,
    }));

    return mappedOptions;
  } catch (error) {
    console.error("Error fetching cities:", error);
    return [];
  }
};

export default function CitySelector({
  formField,
  maxSelected = 1,
  placeholder = "Search for your city",
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formField: ControllerRenderProps<any>;
  maxSelected?: number;
  placeholder?: string;
}) {
  const { accessToken, setToken, clearToken } = useAuthStore();

  return (
    <MultipleSelector
      {...formField}
      onSearch={(value: string) =>
        citiesSearch(value, accessToken, setToken, clearToken)
      }
      maxSelected={maxSelected}
      placeholder={placeholder}
      hidePlaceholderWhenSelected={true}
      loadingIndicator={
        <p className="py-2 text-center leading-10 text-muted-foreground">
          searching...
        </p>
      }
      emptyIndicator={
        <p className="text-center leading-10 text-gray-600 dark:text-gray-400">
          No results, type to search.
        </p>
      }
    />
  );
}
